import * as angular from 'angular';
import { AppRootScope } from '../../src/typings/AppRootScope';
import { EventTypes, ValidationNotification } from '../../src/typings/models';
import { IResponse } from './typings/IResponse';
import { ApiError, ApiRequest } from './typings/models';

// Executes a provided IRequestConfig provided by the ProxyService
// based on the type of results expected from the calling client.
export class ProxyResponse implements IResponse {

    constructor(
        private rootScope: AppRootScope,
        private q: angular.IQService,
        private log: angular.ILogService,
        private http: angular.IHttpService,
        private config: angular.IRequestConfig,
        private apiRequest: ApiRequest,
        private promiseMade: (promise: angular.IPromise<any>) => void
    ) {
    }

    // Returns the response as a single model.
    public as<TModel>(): angular.IPromise<TModel> {
        var defer = this.q.defer<TModel>();

        var requestPromise = this.http(this.config)
            .then((resp: angular.IHttpPromiseCallbackArg<TModel>) => {

                if (angular.isArray(resp.data)) {
                    throw ({ message: 'Array was returned.  Expected object.' });
                }

                this.processResponse(defer, resp);
            })

            .catch((response) => {
                this.handleError(defer, response);
            });

        this.promiseMade(requestPromise);
        return defer.promise;
    }

    // Returns the response as an array of models.
    public asArray<TModel>(): angular.IPromise<TModel[]> {
        var defer = this.q.defer<TModel[]>();

        var requestPromise = this.http(this.config)
            .then((resp: angular.IHttpPromiseCallbackArg<TModel[]>) => {

                if (!angular.isArray(resp.data)) {
                    throw ({ message: 'Array was not returned.' });
                }

                this.processResponse(defer, resp);
            })

            .catch((response) => {
                this.handleError(defer, response);
            });

        this.promiseMade(requestPromise);
        return defer.promise;
    }

    // Executes the response returning a 402 status code.
    public asNoContent(): angular.IPromise<void> {
        var defer = this.q.defer<void>();

        var requestPromise = this.http(this.config)
            .then((resp: angular.IHttpPromiseCallbackArg<any>) => {
                this.processResponse(defer, resp);
            })

            .catch((response) => {
                this.handleError(defer, response);
            });

        this.promiseMade(requestPromise);
        return defer.promise;
    }

    private logResponse(config: any, resp: any, status: number, headers: string) {
        this.log.debug({
            action: 'debug http response',
            apiRequest: this.apiRequest,
            config: config,
            data: resp,
            headers: headers,
            status: status
        });
    }

    // NOTE:  The server should not be returning a HTTP status code of 200.
    // This will be corrected once the existing application is no longer used.
    private processResponse(defer: any, result: any) {
        let brokenRules;

        if (this.isValidationErrorResponse(result)) {
            brokenRules = result.validation;
        } else if (this.isValidationErrorResponse(result.data)) {
            brokenRules = result.data.validation;
        }

        if (brokenRules) {
            this.displayValidationErrors(brokenRules);

            defer.reject(brokenRules);
        }

        // this flag is set in reloadrconfig.js
        // only when livereload task is running
        if ((window as any).IS_LIVERELOAD) {
            this.logResponse(this.config, result.data, result.status, result.headers());
        }
        defer.resolve(result.data);
    }

    private displayValidationErrors(brokenRules) {
        var event = {
            validations: brokenRules
        } as ValidationNotification;

        this.rootScope.publish(
            EventTypes.DisplayValidationErrors,
            event
        );
    }

    private handleError(defer: any, response) {
        var apiError =  {
            apiRequest: this.apiRequest,
            config: this.config
        } as ApiError;

        defer.reject(apiError);
        throw (apiError);
    }

    // Detects if the return response matches the structure of
    // a server-side validation error.  This should be corrected
    // by having the server return the correct HTTP status code. :(
    private isValidationErrorResponse(result: any) {

        if (!result) { return false; }

        return angular.isDefined(result)
            && angular.isDefined(result.success)
            && angular.isDefined(result.validation)
            && angular.isArray(result.validation)
            && result.validation.length > 0;
    }
}
